import React from 'react'
import useBuildScenarioStore, {
  useInitialCleanModelOutput,
  useLastRemoteModelOutput,
} from '../../stores/buildScenarioStore'
import {
  Container,
  NumbersContainer,
  TrippleWrapper,
  VStack,
  TopText,
  MiddleText,
  BottomText,
} from './styles/BuildScenarioNumbers.styles'

const BuildScenarioNumbers = () => {
  const { output: initialCleanOutput } = useInitialCleanModelOutput()
  const lastRemoteOutput = useLastRemoteModelOutput()
  const scenarios = useBuildScenarioStore((state) => state.scenarios)

  // Early return if required data is not available yet
  const hasInitialCleanOutput =
    initialCleanOutput?.scenarios?.[0]?.observationValues?.length > 0

  if (!hasInitialCleanOutput) {
    return <div style={{ display: 'none' }} />
  }

  // Transform initial clean output to originalValues format
  const getOriginalValues = () => {
    if (!initialCleanOutput?.scenarios?.[0]?.observationValues?.length) {
      return null
    }
    const lastValue =
      initialCleanOutput.scenarios[0].observationValues[
        initialCleanOutput.scenarios[0].observationValues.length - 1
      ]
    return {
      name: 'Predicted',
      number: lastValue,
    }
  }

  // Transform scenario data to scenarios format
  const getTransformedScenarios = () => {
    if (!lastRemoteOutput?.scenarios || !scenarios.length) {
      return []
    }

    return lastRemoteOutput.scenarios.map((remoteScenario) => {
      // Find the scenario name by ID
      const scenario = scenarios.find((s) => s.id === remoteScenario.scenarioId)
      const scenarioName =
        scenario?.name || `Scenario ${remoteScenario.scenarioId}`

      // Get the last observation value
      const lastValue = remoteScenario.observationValues?.length
        ? remoteScenario.observationValues[
            remoteScenario.observationValues.length - 1
          ]
        : 0

      return {
        name: scenarioName,
        number: lastValue,
      }
    })
  }

  // Calculate scenario results with percentage differences (previously in BlackScenarioNumbers)
  const getScenarioResults = () => {
    const originalValues = getOriginalValues()
    const transformedScenarios = getTransformedScenarios()

    if (!transformedScenarios?.length || !originalValues?.number) {
      return []
    }

    return transformedScenarios.map((scenario) => {
      // Calculate percentage difference
      const percentageDiff =
        originalValues.number !== 0
          ? (
              ((scenario.number - originalValues.number) /
                originalValues.number) *
              100
            ).toFixed(1)
          : '0.0'

      return {
        name: scenario.name,
        value: scenario.number.toLocaleString(),
        percentageDiff: `${percentageDiff >= 0 ? '+' : ''}${percentageDiff}%`,
      }
    })
  }

  const originalValues = getOriginalValues()
  const scenarioResults = getScenarioResults()

  return (
    <Container>
      <NumbersContainer horizontalMode={false}>
        <TrippleWrapper>
          <VStack>
            <TopText>{originalValues?.name || 'Predicted'}</TopText>
            <MiddleText>
              {originalValues?.number?.toLocaleString() || 'Loading...'}
            </MiddleText>
            <BottomText></BottomText>
          </VStack>
        </TrippleWrapper>
        {scenarioResults.map((result, index) => (
          <TrippleWrapper key={index}>
            <VStack>
              <TopText>{result.name}</TopText>
              <MiddleText>{result.value}</MiddleText>
              <BottomText>{result.percentageDiff}</BottomText>
            </VStack>
          </TrippleWrapper>
        ))}
      </NumbersContainer>
    </Container>
  )
}

export default BuildScenarioNumbers
